import { tt } from '@agos/shared'

/**
 * Which lines in this basket will stop the receipt — §9.111.
 *
 * <p><b>The failure this prevents is a 400 at payment.</b> `Zálohový obal` sits on VAT rate D, and
 * `vat_categories.fiskal_slot` is NULL for C and D, so `FiskalReceiptService.slotFor` refuses the
 * letter rather than guessing an index — correctly, since a guess registers the wrong tax on a real
 * document. The consequence is that an order carrying the deposit cannot be fiskalised, and today the
 * till finds out with a customer standing at it.
 *
 * <p><b>A warning, not a block.</b> The order can still be taken and often should be; what must not
 * happen is discovering it at payment. The real fix is two numbers off `FRINFO` typed into
 * `/vat-categories`, and when every rate in use has an index this returns nothing at all.
 *
 * <p><b>It names the products.</b> *"Sadzba D nemá index"* is true and useless to whoever is holding
 * the till — the useful sentence is which items in this basket are the problem.
 */

export interface SurchargeLike {
  id?: number
  name?: string
  vatCategoryId?: number | null
  isActive?: boolean
}

export interface VatCategoryLike {
  id?: number
  letter?: string
  fiskalSlot?: number | null
  isActive?: boolean
}

export interface ProductLike {
  id?: number
  name?: string
  surchargeIds?: number[]
}

export interface BasketLine {
  productId?: number
  name?: string
}

/** Surcharge ids whose VAT category has no confirmed index on the register. */
export function unprintableSurchargeIds(
  surcharges: readonly SurchargeLike[],
  categories: readonly VatCategoryLike[],
): Set<number> {
  const cannotPrint = new Set(
    categories
      .filter((category) => typeof category.fiskalSlot !== 'number')
      .map((category) => category.id)
      .filter((id): id is number => typeof id === 'number'),
  )
  return new Set(
    surcharges
      .filter(
        (surcharge) =>
          surcharge.isActive !== false &&
          typeof surcharge.vatCategoryId === 'number' &&
          cannotPrint.has(surcharge.vatCategoryId),
      )
      .map((surcharge) => surcharge.id)
      .filter((id): id is number => typeof id === 'number'),
  )
}

/**
 * The product names in this basket that carry one, in basket order, without repeats.
 *
 * <p>A basket with the same pizza on three lines should name it once — the reader is scanning for
 * *which dish*, not counting lines.
 */
export function unprintableProductNames(
  basket: readonly BasketLine[],
  products: readonly ProductLike[],
  blocked: ReadonlySet<number>,
): string[] {
  if (blocked.size === 0) return []
  const byId = new Map<number, ProductLike>()
  for (const product of products) {
    if (typeof product.id === 'number') byId.set(product.id, product)
  }

  const names: string[] = []
  for (const line of basket) {
    if (typeof line.productId !== 'number') continue
    const product = byId.get(line.productId)
    if (!product?.surchargeIds?.some((id) => blocked.has(id))) continue
    const name = (product.name ?? line.name ?? '').trim()
    if (name !== '' && !names.includes(name)) names.push(name)
  }
  return names
}

/** What the banner says, or `null` when every rate in the basket can print. */
export function unprintableWarning(names: readonly string[]): string | null {
  if (names.length === 0) return null
  return `${tt('Doklad sa nevytlačí kvôli')} ${names.join(', ')} - ${tt('príplatok na tejto položke má sadzbu DPH bez indexu na pokladni. Objednávku môžete prijať, ale platba cez kasu zlyhá. Doplňte index v Sadzbách DPH.')}`
}
